import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Biscuit Brand Logo Component
 * 
 * Variants:
 * - full: Cookie icon + "BISCUIT BRAND" text + tagline
 * - compact: Cookie icon + "BISCUIT BRAND" text (no tagline)
 * - icon: Cookie icon only
 * - text: Text only (no icon)
 * 
 * Themes:
 * - light: For dark backgrounds (cream text)
 * - dark: For light backgrounds (brown text)
 */

const BiscuitBrandLogo = ({ 
  variant = 'compact', 
  theme = 'dark',
  size = 'md',
  showTagline = false,
  className = '',
  linkTo = '/',
  onClick
}) => {
  // Size configurations
  const sizes = {
    sm: { icon: 24, text: 'text-lg', tagline: 'text-[8px]', gap: 'gap-1.5' },
    md: { icon: 32, text: 'text-xl', tagline: 'text-[9px]', gap: 'gap-2' },
    lg: { icon: 40, text: 'text-2xl', tagline: 'text-[10px]', gap: 'gap-2.5' },
    xl: { icon: 48, text: 'text-3xl', tagline: 'text-[11px]', gap: 'gap-3' },
  };

  const config = sizes[size] || sizes.md;

  // Theme colors
  const colors = {
    light: {
      biscuit: '#FFF6E9',
      brand: '#D48A2A',
      tagline: '#C4A97A',
    },
    dark: {
      biscuit: '#3A2412',
      brand: '#D48A2A',
      tagline: '#8B7355',
    },
  };

  const themeColors = colors[theme] || colors.dark;

  // Cookie Icon SVG
  const CookieIcon = ({ iconSize }) => (
    <svg 
      viewBox="0 0 100 100" 
      xmlns="http://www.w3.org/2000/svg" 
      width={iconSize} 
      height={iconSize}
      className="flex-shrink-0"
    >
      <circle cx="50" cy="50" r="42" fill="#D48A2A"/>
      <circle cx="82" cy="25" r="20" fill={theme === 'light' ? '#3A2412' : '#FFF6E9'}/>
      <circle cx="32" cy="40" r="6" fill="#3A2412"/>
      <circle cx="55" cy="60" r="5" fill="#3A2412"/>
      <circle cx="38" cy="65" r="4.5" fill="#3A2412"/>
      <circle cx="58" cy="38" r="5" fill="#3A2412"/>
      <circle cx="28" cy="60" r="4" fill="#3A2412"/>
      <circle cx="48" cy="78" r="4" fill="#3A2412"/>
      <circle cx="68" cy="68" r="4" fill="#3A2412"/>
    </svg>
  );

  // Logo Content
  const LogoContent = () => (
    <div className={`flex items-center ${config.gap} ${className}`}>
      {variant !== 'text' && (
        <CookieIcon iconSize={config.icon} />
      )}
      
      {variant !== 'icon' && (
        <div className="flex flex-col">
          <span 
            className={`font-serif font-bold ${config.text} tracking-wide leading-none`}
            style={{ fontFamily: 'Georgia, serif' }}
          >
            <span style={{ color: themeColors.biscuit }}>BISCUIT</span>
            {' '}
            <span style={{ color: themeColors.brand }}>BRAND</span>
          </span>
          
          {(showTagline || variant === 'full') && (
            <span 
              className={`${config.tagline} tracking-[0.2em] uppercase mt-0.5`}
              style={{ color: themeColors.tagline }}
            >
              Discover Biscuits Worldwide
            </span>
          )}
        </div>
      )}
    </div>
  );

  // If linkTo is provided, wrap in Link
  if (linkTo) {
    return (
      <Link 
        to={linkTo} 
        className="flex-shrink-0 hover:opacity-90 transition-opacity"
        data-testid="biscuit-brand-logo"
        onClick={onClick}
      >
        <LogoContent />
      </Link>
    );
  }

  return <LogoContent />;
};

export default BiscuitBrandLogo;
